import React, { useContext, useEffect, useRef } from 'react';

import { useStudentDefaultLookupSearchFields } from 'components/GMetrixTable/gmetrixTableSearchFields';
import { StudentGmetrixTable } from 'components/StudentGmetrixTable/StudentGmetrixTable';
import { StudentsContext } from 'pages/students/context/StudentsDefaultContext';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { NewStudentForm } from 'components/NewStudentForm/NewStudentForm';
import { Student, StudentApi, StudentSortField } from 'apis/StudentApi';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { currentLookupUrl, getStringFromURL } from 'utils/other';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSuperState, useUrlFilters } from 'hooks';
import { useNotification } from 'app/notifications';
import { PageWithHeader } from 'components/layout';
import { helpContent } from 'pages/students/help';
import { SortDescriptor } from 'utils/dataQuery';
import { KendoIntlProvider } from 'app/culture';
import { useNavigate } from 'react-router-dom';
import { Card, Modal } from 'react-bootstrap';
import { useJoyride } from 'app/joyride';
import { useApp } from 'app';

import 'styles/global-styles.scss';
import 'styles/kendo-default-theme-with-overrides.scss';

export const Students = () => {

    // Persist the view (filters, search, sort, pagination) in the URL query string so
    // navigating away and back restores the same view, then refetch fresh student rows.
    const urlFilters = useUrlFilters({
        archived: false,
        searchterm: '',
        skip: 0,
        sortdir: '',
        sortfield: '',
        take: 15
    });

    const [state, setState] = useSuperState({
        filteredStudents: [] as Student[],
        filtersInitialized: false,
        loadingGridData: true,
        pageLoading: false,
        paginationSkip: urlFilters.initial.skip,
        paginationTake: urlFilters.initial.take,
        searchTerm: urlFilters.initial.searchterm,
        selectedStudents: [] as Student[],
        serverTotalCount: 0,
        showArchived: urlFilters.initial.archived,
        showNewStudentModal: false,
        sortDir: urlFilters.initial.sortdir,
        sortField: urlFilters.initial.sortfield as StudentSortField | '',
        students: [] as Student[]
    });

    const { userPartners } = useApp();
    const { filtersInitialized, paginationSkip, paginationTake, searchTerm, selectedStudents, serverTotalCount, showArchived, sortDir, sortField, students } = state;

    const navigate = useNavigate();

    const { formatMessage, locale } = useIntl();
    const studentDefaultLookupSearchFields = useStudentDefaultLookupSearchFields();
    const { joyrideTours, loadCurrentTour } = useJoyride();
    const { createToast } = useNotification();
    const { countries, permissions } = useContext(StudentsContext);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isInitialSearchRef = useRef(true);

    useEffect(() => {

        onStudentInUrl();

    }, []);

    useEffect(() => {

        if (joyrideTours.length > 0 && !state.pageLoading) {

            loadCurrentTour('PageLoad');

        }

    }, [joyrideTours]);

    // Debounced search for server-side pagination
    useEffect(() => {

        // The first server call is kicked off by the table filters on mount.
        // Skip the first debounced run to avoid duplicate requests.
        if (isInitialSearchRef.current) {

            isInitialSearchRef.current = false;
            return;

        }

        if (searchDebounceRef.current) {

            clearTimeout(searchDebounceRef.current);

        }

        setState({ loadingGridData: true });

        searchDebounceRef.current = setTimeout(() => {

            setState({ paginationSkip: 0 });
            fetchStudentsPaged(0, paginationTake, sortField, sortDir, searchTerm, showArchived);

        }, 1000);

        return () => {

            if (searchDebounceRef.current) {

                clearTimeout(searchDebounceRef.current);

            }

        };

    }, [searchTerm]);

    const fetchStudentsPaged = async (skip: number, take: number, sField: string, sDir: string, search: string, archived: boolean) => {

        try {

            setState({ loadingGridData: true });
            const page = Math.floor(skip / take) + 1;
            const result = await StudentApi.GetAllPaged(page, take, sField, sDir, search, archived);
            setState({ serverTotalCount: result.TotalCount, students: result.Items });
            urlFilters.write({
                archived,
                searchterm: search,
                skip,
                sortdir: sDir,
                sortfield: sField,
                take
            });

        } catch (e) {

            console.error(e);
            const message = typeof e === 'string' ? e : (e as any)?.message || (e as any)?.Message || '';
            if (message.toLowerCase().includes('timed out') || message.toLowerCase().includes('timeout')) {

                createToast(formatMessage({ id: 'the_request_timed_out_please_try_again.93e77e8d', defaultMessage: 'The request timed out. Please try again.' }), 'danger');

            } else {

                createToast(formatMessage({ id: 'failed_to_load_student_data_please_try_a.1ec58ad0', defaultMessage: 'Failed to load student data. Please try again.' }), 'danger');

            }

        } finally {

            setState({ loadingGridData: false });

        }

    };

    const handleServerPageChange = (skip: number, take: number) => {

        setState({ paginationSkip: skip, paginationTake: take });
        fetchStudentsPaged(skip, take, sortField, sortDir, searchTerm, showArchived);

    };

    const handleServerSortChange = (sort: SortDescriptor[]) => {

        const newSortField = (sort.length > 0 ? sort[0].field : '') as StudentSortField | '';
        const newSortDir = sort.length > 0 ? (sort[0].dir || '') : '';
        setState({ sortDir: newSortDir, sortField: newSortField });
        fetchStudentsPaged(paginationSkip, paginationTake, newSortField, newSortDir, searchTerm, showArchived);

    };

    const handleFilterChange = (filters: { showArchived: boolean }) => {

        // The table emits its filters once on mount and again whenever the user toggles one.
        // On the first (mount) emission, restore the previous page/sort/search and refetch the
        // exact view the user left; on later emissions the filter actually changed, so reset to
        // the first page.
        if (!filtersInitialized) {

            setState({ filtersInitialized: true, showArchived: filters.showArchived });
            fetchStudentsPaged(paginationSkip, paginationTake, sortField, sortDir, searchTerm, filters.showArchived);
            return;

        }

        setState({ paginationSkip: 0, showArchived: filters.showArchived });
        fetchStudentsPaged(0, paginationTake, sortField, sortDir, searchTerm, filters.showArchived);

    };

    const onStudentInUrl = async () => {

        // this means create mode
        if (getStringFromURL('create') === 'true') {

            setState({ showNewStudentModal: true });

        }

    };

    const onDetailsItemClick = (dataItem: Student) => {

        navigate(`/students/${dataItem.Id}`, { state: { from: currentLookupUrl() } });

    };

    const handleShowModal = () => setState({ showNewStudentModal: true });

    const newStudentActions = permissions.Import ? [
        {
            label: formatMessage({ id: 'manual.4cc90d81', defaultMessage: 'Manual' }),
            onClick: handleShowModal
        },
        {
            label: formatMessage({ id: 'import.3eab33a0', defaultMessage: 'Import' }),
            to: '/students/import'
        }
    ] : [];

    const headerActions = permissions.Merge ? [
        {
            label: (
                <div className={'d-inline-flex align-items-center justify-content-center gap-1'}>
                    <i className={'fa-solid fa-code-merge'} />
                    <span>
                        <FormattedMessage defaultMessage="Merge Students" id="merge_students.8704ea51" />
                    </span>
                </div>
            ),
            to: '/students/merge',
            variant: 'outline-primary'
        }
    ] : [];

    return (
        <KendoIntlProvider>
            <LocalizationProvider language={locale}>
                <IntlProvider locale={locale}>

                    <PageWithHeader
                        headerConfig={{
                            actions: headerActions,
                            cta: {
                                actions: newStudentActions,
                                disabled: newStudentActions.length === 0,
                                hidden: newStudentActions.length === 0,
                                label: (
                                    <div className={'d-inline-flex align-items-center justify-content-center flex-fill gap-1'}>
                                        <i className={'fa-solid fa-plus'} />
                                        <span>
                                            <FormattedMessage defaultMessage="New Student" id="new_student.411d2e15" />
                                        </span>
                                    </div>
                                )
                            },
                            helpContent: helpContent,
                            title: formatMessage({ id: 'students.0933e5a0', defaultMessage: 'Students' })
                        }}
                    >

                        <div className="students d-flex flex-column gap-3">
                            <Card body>
                                {state.pageLoading ?
                                    (
                                        <GMetrixLoader />
                                    )
                                    :
                                    (
                                        <StudentGmetrixTable
                                            allowArchived
                                            manuallyRenderStudents
                                            serverSidePagination
                                            columnData={[
                                                { field: 'Firstname', sortField: 'FirstName', title: formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' }) },
                                                { field: 'Lastname', sortField: 'LastName', title: formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' }) },
                                                { field: 'Username', sortField: 'UserName', title: formatMessage({ id: 'username.dceab582', defaultMessage: 'Username' }) },
                                                { field: 'Email', sortField: 'EmailAddress', title: formatMessage({ id: 'email.5cbb5845', defaultMessage: 'Email', description: 'Formulario' }) },
                                                { field: 'StudentNumber', title: formatMessage({ id: 'student_number.4ed64306', defaultMessage: 'Student Number' }) },
                                                {
                                                    cell: ({ dataItem }) => (
                                                        <td>
                                                            {dataItem.IsAda ? (
                                                                <i className="fa-solid fa-check text-success fa-lg"></i>
                                                            ) : (
                                                                <i className="fa-solid fa-dash fa-lg"></i>
                                                            )}
                                                        </td>
                                                    ),
                                                    field: 'IsAda',
                                                    sortField: 'HasAccommodations',
                                                    title: formatMessage({ id: 'accessibility.0c8f244d', defaultMessage: 'Accessibility' })
                                                },
                                                {
                                                    defaultSortDir: 'desc',
                                                    field: 'LastAccessDate',
                                                    title: formatMessage({ id: 'last_login.0b7d4377', defaultMessage: 'Last Login' })
                                                }
                                            ]}
                                            advancedSearchFields={studentDefaultLookupSearchFields}
                                            allowExport={false}
                                            archivedStudents={permissions.ArchivedStudents}
                                            defaultItemsPerPage={paginationTake}
                                            initialSkip={paginationSkip}
                                            loadingGridData={state.loadingGridData}
                                            onDetailsItemClick={permissions.Search ? onDetailsItemClick : undefined}
                                            onFilterChange={handleFilterChange}
                                            onSelectedChange={val => setState({ selectedStudents: val })}
                                            onServerPageChange={handleServerPageChange}
                                            onServerSortChange={handleServerSortChange}
                                            permissions={permissions}
                                            searchTerm={state.searchTerm}
                                            selectableRows={false}
                                            selectedStudents={selectedStudents}
                                            serverTotalCount={serverTotalCount}
                                            setLoadingGridData={val => setState({ loadingGridData: val })}
                                            setSearchTerm={val => setState({ searchTerm: val })}
                                            setStudents={val => setState({ students: val })}
                                            students={students}
                                            toggleColumns={true}
                                        />
                                    )
                                }
                            </Card>
                        </div>

                    </PageWithHeader>

                    <Modal onHide={() => setState({ showNewStudentModal: false })} show={state.showNewStudentModal} size="xl" >
                        <Modal.Header
                            closeButton
                        >
                            <Modal.Title className="w-100">
                                <FormattedMessage defaultMessage="New Student" id="new_student.411d2e15" />
                            </Modal.Title>
                        </Modal.Header>
                        <NewStudentForm
                            onSuccess={student => {

                                setState({ showNewStudentModal: false });
                                const url = new URL(window.location.href);
                                const search_params = url.searchParams;
                                search_params.delete('create');
                                url.search = search_params.toString();
                                window.history.replaceState(null, '', url);
                                onDetailsItemClick(student);
                                fetchStudentsPaged(paginationSkip, paginationTake, sortField, sortDir, searchTerm, showArchived);

                            }}
                            ActionsWrapper={Modal.Footer}
                            ContentWrapper={Modal.Body}
                            countries={countries}
                            onClose={() => setState({ showNewStudentModal: false })}
                            permissions={permissions}
                            userPartners={userPartners}
                        />

                    </Modal>

                </IntlProvider>
            </LocalizationProvider>
        </KendoIntlProvider>

    );

};
